import {Controller} from "@hotwired/stimulus";

// Connects to data-controller="event-setting"
export default class extends Controller {
  static targets = ["taxFee", "refundPolicy", "taxFeeField", "refundPolicyField", "refundEnabledField", "customRefundPolicyContainer", "customRefundPolicyText", "privacyLevel", "privacyDescription", "passwordFields", "accessPassword", "switchConfirm", "switchEffect", "timeLimit"];

  static values = { hasQuestions: Boolean };

  connect() {
    // Snapshot the checkout-type radio that's checked at load, so a confirmed-
    // cancel can revert to it without a server round-trip.
    this.previousCheckoutRadio = this.checkedCheckoutRadio();

    // Defer the themed-select init to the next animation frame so it runs AFTER
    // the global turbo:load select2 init (custom.js) — see initThemedSelects for
    // the full double-init root-cause note. Running last lets us reset whatever
    // the global init created and own these pickers cleanly.
    this._themedSelectFrame = requestAnimationFrame(() => this.initThemedSelects());
  }

  disconnect() {
    if (this._themedSelectFrame) cancelAnimationFrame(this._themedSelectFrame);
  }

  // ── Privacy + Registration-time-limit pickers (themed select2) ─────────────
  // Both are themed DS select2 pickers (no native browser-blue <select>), scoped
  // via the .select2-search-off wrapper + .es-themed-dropdown panel class.
  //
  // DOUBLE-INIT GUARD (mirrors event_widget_configurator_controller.js): these
  // <select>s carry the specific .es-themed-select class — NOT a bare .select2 —
  // so the global turbo:load init (custom.js: `$('.select2').each(...)`) never
  // grabs the <select> itself. But once select2 renders, the generated
  // .select2-container SPAN gets stamped with a `.select2` class, and a later
  // global `$('.select2')` pass would re-init THAT span, stacking a second,
  // empty/broken picker ("No results found" / stray search box). So for each
  // themed select we: (1) destroy any existing instance, (2) hard-remove any
  // rendered .select2-container span in the wrapper, (3) clear select2's
  // bookkeeping attrs/classes back to a pristine <select>, (4) re-init ONCE, then
  // (5) strip `.select2` off our rendered container so the global pass can't find
  // it. Deferred to a rAF so it runs after the global init.
  initThemedSelects() {
    if (typeof window.initSelect2 !== "function" && !(window.jQuery && window.jQuery.fn.select2)) return;

    this.element.querySelectorAll(".es-themed-select").forEach((el) => {
      el.classList.remove("select2");
      if (window.jQuery) {
        const $el = window.jQuery(el);
        if ($el.data("select2")) $el.select2("destroy");
      }
      const wrap = el.closest(".select2-search-off") || el.parentElement;
      if (wrap) wrap.querySelectorAll(".select2-container").forEach((c) => c.remove());
      el.classList.remove("select2-hidden-accessible");
      el.removeAttribute("aria-hidden");
      el.removeAttribute("tabindex");
      el.removeAttribute("data-select2-id");
      el.querySelectorAll("[data-select2-id]").forEach((o) => o.removeAttribute("data-select2-id"));

      const opts = {
        minimumResultsForSearch: Infinity,
        allowClear: false,
        dropdownCssClass: "es-themed-dropdown",
        width: "100%",
        // Preserve the authored option order. The global initSelect2 wrapper
        // applies an alphabetic/numeric sorter, which would reorder Privacy
        // ("Private…" before "Public Event") and isn't wanted for the fixed,
        // intentionally-ordered duration / privacy lists. Identity sorter keeps
        // DOM order.
        sorter: (data) => data,
        // Render each option (and the selected trigger) with its pre-rendered
        // lucide SVG from <option data-icon-svg> (set server-side via the
        // lucide_icon helper, so the file is guaranteed vendored).
        templateResult: this.iconOptionTemplate.bind(this),
        templateSelection: this.iconOptionTemplate.bind(this),
        escapeMarkup: (m) => m,
      };

      if (window.jQuery && window.jQuery.fn.select2) {
        // Call select2 directly (not the global initSelect2 wrapper) so our
        // identity sorter isn't clobbered by the wrapper's alphabetic sorter.
        window.jQuery(el).select2(opts);
      }

      // Strip `.select2` from the container span select2 just rendered so the
      // global turbo:load pass (which runs after us) can't re-init it.
      if (wrap) wrap.querySelectorAll(".select2-container.select2").forEach((c) => c.classList.remove("select2"));

      // The privacy picker drives password-field reveal + the description copy.
      // select2 fires a jQuery `change`; bind via jQuery's select2:select too so
      // the handler fires regardless of native-event propagation (belt-and-
      // suspenders — the data-action native `change` covers the no-select2 path).
      if (this.hasPrivacyLevelTarget && el === this.privacyLevelTarget && window.jQuery) {
        window.jQuery(el).on("select2:select", () => this.handlePrivacyChange({ currentTarget: el }));
      }
    });
  }

  // select2 templateResult/templateSelection: prepend the option's pre-rendered
  // lucide SVG (from <option data-icon-svg>) before its label. escapeMarkup is a
  // passthrough, so we emit our own escaped markup string.
  iconOptionTemplate(state) {
    if (!state || !state.id) return state ? state.text : "";
    const svg = state.element ? state.element.getAttribute("data-icon-svg") : null;
    const label = this.escapeHtml(state.text);
    return svg
      ? `<span class="es-opt"><span class="es-opt-ic">${svg}</span><span>${label}</span></span>`
      : `<span class="es-opt">${label}</span>`;
  }

  escapeHtml(s) {
    const d = document.createElement("div");
    d.textContent = s == null ? "" : String(s);
    return d.innerHTML;
  }

  // ── Checkout-type switch warning (§05) ─────────────────────────────────────
  // Switching personalized ↔ non-personalized changes how every question is
  // collected (per attendee vs once per order). When questions already exist we
  // confirm the effect before letting the change stand; on cancel we revert the
  // radio. Fresh events (no questions) switch with no nag.
  checkoutTypeRadios() {
    return Array.from(this.element.querySelectorAll('input[type="radio"][name*="[personalized]"]'));
  }

  checkedCheckoutRadio() {
    return this.checkoutTypeRadios().find((r) => r.checked) || null;
  }

  confirmCheckoutTypeSwitch(event) {
    const radio = event.currentTarget;

    // No questions yet → no nag; just keep the snapshot current.
    if (!this.hasQuestionsValue) {
      this.previousCheckoutRadio = radio;
      return;
    }

    // No actual change (re-selecting the same value) → nothing to warn about.
    if (this.previousCheckoutRadio && radio.value === this.previousCheckoutRadio.value) {
      return;
    }

    this.pendingCheckoutRadio = radio;

    // value === "true" → switching TO personalized (per-attendee);
    // value === "false" → switching TO non-personalized (per-order).
    const toPersonalized = radio.value === "true";
    if (this.hasSwitchEffectTarget) {
      const el = this.switchEffectTarget;
      el.innerHTML = toPersonalized
        ? el.dataset.toPerAttendee
        : el.dataset.toPerOrder;
    }
    this.openSwitchConfirm();
  }

  openSwitchConfirm() {
    if (!this.hasSwitchConfirmTarget) return;
    this.switchConfirmTarget.hidden = false;
    this.switchConfirmTarget.classList.add("is-open");
  }

  closeSwitchConfirm() {
    if (!this.hasSwitchConfirmTarget) return;
    this.switchConfirmTarget.hidden = true;
    this.switchConfirmTarget.classList.remove("is-open");
  }

  acceptCheckoutTypeSwitch() {
    // Keep the new selection; advance the snapshot so a later cancel reverts here.
    if (this.pendingCheckoutRadio) {
      this.previousCheckoutRadio = this.pendingCheckoutRadio;
      this.pendingCheckoutRadio = null;
    }
    this.closeSwitchConfirm();
  }

  cancelCheckoutTypeSwitch() {
    // Revert to the previously-selected checkout type.
    if (this.previousCheckoutRadio) {
      this.previousCheckoutRadio.checked = true;
    }
    this.pendingCheckoutRadio = null;
    this.closeSwitchConfirm();
  }

  handleTaxSelection(event) {
    if (!event.currentTarget.checked) {
      this.taxFeeTarget.value = '';
      this.taxFeeFieldTarget.value = '';
      this.taxFeeTarget.disabled = true;
    } else {
      this.taxFeeTarget.disabled = false;
    }
  }

  updateTaxValue(event){
    this.taxFeeFieldTarget.value = event.currentTarget.value
  }

  handleRefundPolicySelection(event) {
    if (!event.currentTarget.checked) {
      this.refundPolicyTarget.disabled = true;
      this.refundEnabledFieldTarget.value = 'false';
      this.hideCustomRefundPolicyField();
    } else {
      // Enable refunds
      this.refundPolicyTarget.disabled = false;
      this.refundEnabledFieldTarget.value = 'true';
      // Keep the current refund option value
      this.refundPolicyFieldTarget.value = this.refundPolicyTarget.value;
      // Show custom field if needed
      if (this.refundPolicyTarget.value === 'custom_refund_policy') {
        this.showCustomRefundPolicyField();
      }
    }
  }

  updateRefundOption(event) {
    const selectedValue = event.currentTarget.value;
    this.refundPolicyFieldTarget.value = selectedValue;
    
    // Show/hide custom refund policy text field based on selection
    if (selectedValue === 'custom_refund_policy') {
      this.showCustomRefundPolicyField();
    } else {
      this.hideCustomRefundPolicyField();
    }
  }

  showCustomRefundPolicyField() {
    if (this.hasCustomRefundPolicyContainerTarget) {
      this.customRefundPolicyContainerTarget.style.display = 'block';
    }
  }

  hideCustomRefundPolicyField() {
    if (this.hasCustomRefundPolicyContainerTarget) {
      this.customRefundPolicyContainerTarget.style.display = 'none';
    }
  }

  handlePrivacyChange(event) {
    const selectedValue = event.currentTarget.value;

    // Update privacy description
    if (this.hasPrivacyDescriptionTarget) {
      const descriptions = {
        'public_event': 'Visible to everyone and searchable',
        'private_link': 'Hidden from search, accessible via direct link',
        'private_password': 'Password protected - requires access code'
      };
      this.privacyDescriptionTarget.textContent = descriptions[selectedValue] || '';
    }

    // Show/hide password fields based on selection
    if (this.hasPasswordFieldsTarget) {
      if (selectedValue === 'private_password') {
        this.passwordFieldsTarget.style.display = 'block';
        // Clear any validation errors when showing
        if (this.hasAccessPasswordTarget) {
          this.accessPasswordTarget.classList.remove('is-invalid');
        }
      } else {
        this.passwordFieldsTarget.style.display = 'none';
        // Clear password field when not needed
        if (this.hasAccessPasswordTarget) {
          this.accessPasswordTarget.value = '';
        }
      }
    }
  }
}
